import { ANNUAL_MONTHS, PLANS } from './plans';
import { BRAND_NAME } from '@/config/brand';
import { SITE_URL } from '@/lib/brand';

/**
 * The SoftwareApplication JSON-LD of /pricing. Its offers are built from the same plans.data.json as the visible pricing table
 * (through PLANS), so the structured data cannot show a price the page does not: scripts/prerender-checks.mjs and the tests compare the
 * two. Prices are bare decimals ("49.99") with priceCurrency, as schema.org asks. Web only: there are no app-store listings, and
 * there is no free plan (the 14-day trial is not an Offer).
 */
const decimal = (cents: number) => (cents / 100).toFixed(2);

const offer = (name: string, interval: string, cents: number) => ({
  '@type': 'Offer',
  name: `${name} (${interval})`,
  price: decimal(cents),
  priceCurrency: 'USD',
  url: `${SITE_URL}/pricing`,
});

const softwareApplication = (offers: ReturnType<typeof offer>[]) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: BRAND_NAME,
  description: 'Quotes, scheduling and invoices software for service businesses',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  brand: { '@type': 'Brand', name: BRAND_NAME },
  offers,
});

export function pricingSoftwareSchema() {
  return softwareApplication(PLANS.flatMap((p) => [offer(p.name, 'monthly', p.monthly), offer(p.name, 'annual', p.monthly * ANNUAL_MONTHS)]));
}

/**
 * The SoftwareApplication JSON-LD of the home page: the same application as /pricing with only the three monthly offers, because the home
 * page's plans table opens on monthly (annual is one click away and not on the page as first shown). No trial, rating or review markup:
 * the home page states none of them as an Offer, and the 14-day trial is not an Offer (see above).
 */
export function homeSoftwareSchema() {
  return softwareApplication(PLANS.map((p) => offer(p.name, 'monthly', p.monthly)));
}
